/**
 * Couches de marqueurs.
 *
 * Empilement, du dessous vers le dessus :
 *   *-annot   cerne gris des fiches documentées
 *   hosp-sq / gmfu-tri / gmf-dots   les marqueurs
 *   sel-*     copie agrandie du marqueur sélectionné
 *   hov-*     calque de survol, un seul élément
 *
 * Le survol a ses propres couches plutôt que d'agrandir le marqueur d'origine :
 * il faut animer une taille image par image, et la modifier sur les couches de
 * base l'appliquerait aux 705 points d'un coup.
 */

import { SQUARE_IMAGE, TRIANGLE_IMAGE } from './icons.js'
import { CIRCLE_RADIUS, EMPHASIS_RATIO, SQUARE_SIZE, TRIANGLE_SIZE, toExpression } from './sizing.js'
import { STATUS_COLOR, STROKE_COLOR, STROKE_WIDTH } from './style.js'

export const MARKER_LAYERS = ['gmf-dots', 'gmfu-tri', 'hosp-sq']
export const HOVER_LAYERS = ['hov-c', 'hov-s']
export const SELECTION_LAYERS = ['sel-c', 'sel-t', 'sel-sq']

/** Couches d'un type d'affichage, pour les allumer et les éteindre ensemble. */
export const LAYERS_BY_TYPE = {
  gmf: ['gmf-dots', 'gmf-annot'],
  gmfu: ['gmfu-tri', 'gmfu-annot'],
  hosp: ['hosp-sq', 'hosp-annot'],
}

const annotRing = (id, source, filter, radiusStops) => ({
  id,
  type: 'circle',
  source,
  filter,
  paint: {
    'circle-radius': ['interpolate', ['linear'], ['zoom'], ...radiusStops.flat()],
    'circle-color': 'rgba(0,0,0,0)', // seul le contour compte
    'circle-stroke-color': '#c2c2b8',
    'circle-stroke-width': 1,
  },
})

export function addMarkerLayers(map) {
  map.addLayer(annotRing('hosp-annot', 'hosp', ['==', ['get', 'annotated'], true],
    [[4, 7], [8, 8.5], [12, 11], [15, 13]]))
  map.addLayer(annotRing('gmf-annot', 'gmf',
    ['all', ['==', ['get', 'annotated'], true], ['==', ['get', 'type'], 'GMF']],
    [[4, 4.8], [8, 6], [12, 8], [15, 9.6]]))
  map.addLayer(annotRing('gmfu-annot', 'gmf',
    ['all', ['==', ['get', 'annotated'], true], ['==', ['get', 'type'], 'GMF-U']],
    [[4, 5.5], [8, 7], [12, 9.2], [15, 11]]))

  map.addLayer({
    id: 'hosp-sq',
    type: 'symbol',
    source: 'hosp',
    layout: {
      'icon-image': SQUARE_IMAGE,
      'icon-size': toExpression(SQUARE_SIZE),
      // Les marqueurs ne doivent jamais être escamotés pour cause de
      // chevauchement : un point manquant se lit « pas de clinique ici ».
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
  })

  map.addLayer({
    id: 'gmfu-tri',
    type: 'symbol',
    source: 'gmf',
    filter: ['==', ['get', 'type'], 'GMF-U'],
    layout: {
      'icon-image': TRIANGLE_IMAGE,
      'icon-size': toExpression(TRIANGLE_SIZE),
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
  })

  map.addLayer({
    id: 'gmf-dots',
    type: 'circle',
    source: 'gmf',
    filter: ['==', ['get', 'type'], 'GMF'],
    paint: {
      'circle-radius': toExpression(CIRCLE_RADIUS),
      'circle-color': STATUS_COLOR,
      'circle-stroke-color': STROKE_COLOR,
      'circle-stroke-width': STROKE_WIDTH,
    },
  })

  // Sélection : une copie agrandie du marqueur, à la taille d'un survol. Trois
  // couches et non une : le cercle se dimensionne en `circle-radius`, les
  // symboles en `icon-size`, et triangle et carré n'ont pas le même barème.
  // Statiques, contrairement au survol : rien à animer, la taille est acquise.
  map.addLayer({
    id: 'sel-c',
    type: 'circle',
    source: 'sel',
    filter: ['==', ['get', 'shape'], 'c'],
    paint: {
      'circle-radius': toExpression(CIRCLE_RADIUS, EMPHASIS_RATIO),
      'circle-color': STATUS_COLOR,
      'circle-stroke-color': STROKE_COLOR,
      'circle-stroke-width': STROKE_WIDTH,
    },
  })

  map.addLayer({
    id: 'sel-t',
    type: 'symbol',
    source: 'sel',
    filter: ['==', ['get', 'shape'], 't'],
    layout: {
      'icon-image': ['get', 'image'],
      'icon-size': toExpression(TRIANGLE_SIZE, EMPHASIS_RATIO),
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
  })

  map.addLayer({
    id: 'sel-sq',
    type: 'symbol',
    source: 'sel',
    filter: ['==', ['get', 'shape'], 's'],
    layout: {
      'icon-image': ['get', 'image'],
      'icon-size': toExpression(SQUARE_SIZE, EMPHASIS_RATIO),
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
  })

  map.addLayer({
    id: 'hov-c',
    type: 'circle',
    source: 'hov',
    filter: ['==', ['get', 'shape'], 'c'],
    paint: {
      'circle-radius': 5, // remplacé à chaque image par l'animation
      'circle-color': STATUS_COLOR,
      'circle-stroke-color': STROKE_COLOR,
      'circle-stroke-width': STROKE_WIDTH,
    },
  })

  map.addLayer({
    id: 'hov-s',
    type: 'symbol',
    source: 'hov',
    filter: ['in', ['get', 'shape'], ['literal', ['t', 's']]],
    layout: {
      'icon-image': ['get', 'image'],
      'icon-size': 0.6,
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
  })
}

/**
 * Filtre de statut appliqué aux couches.
 *
 * Chaque couche garde son filtre de type propre, combiné au filtre de statut.
 * Quand tous les statuts sont affichés, on n'ajoute rien : une expression
 * `in` évaluée sur 705 points à chaque image n'a pas à exister pour ne rien
 * exclure.
 */
export function applyStatusFilter(map, statusFilters) {
  const active = Object.keys(statusFilters).filter((s) => statusFilters[s])
  const all = active.length === Object.keys(statusFilters).length
  const statusExpr = all ? null : ['in', ['get', 'status'], ['literal', active]]

  const combine = (base) => {
    if (all) return base
    return base ? ['all', base, statusExpr] : statusExpr
  }

  map.setFilter('gmf-dots', combine(['==', ['get', 'type'], 'GMF']))
  map.setFilter('gmfu-tri', combine(['==', ['get', 'type'], 'GMF-U']))
  map.setFilter('gmf-annot', combine(['all', ['==', ['get', 'annotated'], true], ['==', ['get', 'type'], 'GMF']]))
  map.setFilter('gmfu-annot', combine(['all', ['==', ['get', 'annotated'], true], ['==', ['get', 'type'], 'GMF-U']]))
  map.setFilter('hosp-sq', combine(null))
  map.setFilter('hosp-annot', combine(['==', ['get', 'annotated'], true]))
}

export function applyTypeVisibility(map, typeFilters) {
  for (const [type, layers] of Object.entries(LAYERS_BY_TYPE)) {
    const visibility = typeFilters[type] ? 'visible' : 'none'
    for (const layer of layers) map.setLayoutProperty(layer, 'visibility', visibility)
  }
}
